import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '../../lib/styles';

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
}

function Tooltip({
  label,
  withinPortal = false,
  asChild = false,
  children,
  disabled = false,
  className,
  ...rest
}: React.ComponentProps<typeof TooltipPrimitive.Root> & {
  label?: React.ReactNode;
  withinPortal?: boolean;
  asChild?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  if (disabled) return children;

  if (label) {
    return (
      <TooltipProvider>
        <TooltipPrimitive.Root data-slot="tooltip" {...rest}>
          <TooltipTrigger asChild={asChild}>{children}</TooltipTrigger>
          <TooltipContent withinPortal={withinPortal} className={className}>
            {label}
          </TooltipContent>
        </TooltipPrimitive.Root>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...rest}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" type="button" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  withinPortal = false,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & { withinPortal?: boolean }) {
  let content = (
    <TooltipPrimitive.Content
      data-slot="tooltip-content"
      sideOffset={sideOffset}
      className={cn(
        'bg-gray-15 text-white animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[401] w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance font-medium',
        className,
      )}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow className="bg-gray-15 fill-gray-15 z-[401] size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
    </TooltipPrimitive.Content>
  );

  if (withinPortal) content = <TooltipPrimitive.Portal>{content}</TooltipPrimitive.Portal>;

  return content;
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
